import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const HeroSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('ar');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('memorial-language') || 'ar';
    setCurrentLanguage(savedLanguage);
    document.documentElement.dir = savedLanguage === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = savedLanguage;

    // Listen for language changes
    const handleLanguageChange = (event) => {
      const newLanguage = event.detail?.language || 'ar';
      setCurrentLanguage(newLanguage);
      document.documentElement.dir = newLanguage === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.lang = newLanguage;
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  const heroContent = {
    ar: {
      tagline: "لنكمّل الحكاية… نختتم رحلة الذاكرة في إسطنبول؛",
      title: "عامٌ على النصر",
      date: "السبت 13 ديسمبر",
      location: "İstanbul - Grand Cevahir Hotel",
      subtitle: "في الذكرى التي أعادت للناس يقينهم وارتباطهم بوطنهم، نلتقي لنستعيد اللحظة ونفتح الطريق لما بعدها.",
      ctaIstanbul: "التسجيل لفعالية إسطنبول",
      ctaOtherState: "التسجيل في ولاية أخرى",
      scrollText: "اكتشف المزيد"
    },
    en: {
      tagline: "Let's complete the story... We start from Istanbul and carry the memory to all states;",
      title: "A Year of Victory",
      date: "Saturday, December 13",
      location: "İstanbul - Grand Cevahir Hotel",
      subtitle: "On the anniversary that restored people's certainty and connection to their homeland, we meet to reclaim the moment and open the way for what comes after.",
      ctaIstanbul: "Register for Istanbul Event",
      ctaOtherState: "Register in Another State",
      scrollText: "Discover More"
    },
    tr: {
      tagline: "Hikayeyi tamamlayalım... İstanbul'dan başlıyoruz ve hafızayı tüm illere taşıyoruz;",
      title: "Zaferin Bir Yılı",
      date: "Cumartesi, 13 Aralık",
      location: "İstanbul - Grand Cevahir Hotel",
      subtitle: "İnsanların kesinliklerini ve vatanlarına bağlılıklarını geri kazandıran yıldönümünde, anı yeniden kazanmak ve sonrası için yolu açmak için bir araya geliyoruz.",
      ctaIstanbul: "İstanbul Etkinliğine Kayıt Olun",
      ctaOtherState: "Başka Bir İlde Kayıt Olun",
      scrollText: "Daha Fazla Keşfet"
    }
  };

  const scrollToWhySection = () => {
    const element = document.getElementById('why-we-hold-event');
    if (element) {
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToEngagement = () => {
    const element = document.getElementById('get-involved');
    if (element) {
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToStates = () => {
    const element = document.getElementById('states');
    if (element) {
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const content = heroContent?.[currentLanguage];

  // Apply extra padding based on language on mobile
  const mobilePadding = (currentLanguage === 'en' || currentLanguage === 'tr') ? 'pt-40' : 'pt-28';

  return (
    <section id="hero" className={`relative min-h-screen flex items-center justify-center overflow-hidden ${mobilePadding} md:pt-0`}>
      {/* Video Background */}
      <div className="absolute inset-0 w-full h-full">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="https://res.cloudinary.com/dqsyvuqrs/video/upload/v1767736731/cover_yf508y.mp4" type="video/mp4" />
        </video>
        {/* Overlay for readability */}
        <div className="absolute inset-0 bg-black/60"></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="space-y-8"
        >
          {/* Tagline */}
          {/* <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-body text-lg md:text-xl text-accent italic mb-4"
          >
            {content?.tagline}
          </motion.p> */}

          {/* Memorial Title */}
          <div className="space-y-12">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="font-memorial text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight"
            >
              {content?.title}
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <div className="inline-flex items-center space-x-2 bg-card border border-border rounded-lg px-6 py-3 shadow-memorial">
                <Icon name="Calendar" size={20} className="text-accent" />
                <span className="font-body text-lg md:text-xl font-medium text-text-primary">
                  {content?.date}
                </span>
              </div>
              <a
                href="https://maps.app.goo.gl/mm9Af8WXdpzWV8bh6"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-card border border-border rounded-lg px-6 py-3 shadow-memorial cursor-pointer hover:bg-muted hover:border-primary/50 transition-all duration-200 group"
                title="Click to open location in Google Maps"
              >
                <Icon name="MapPin" size={20} className="text-accent" />
                <span className="font-body text-lg md:text-xl font-medium text-text-primary group-hover:text-primary transition-colors duration-200">
                  {content?.location}
                </span>
                <Icon name="ExternalLink" size={16} className="text-text-secondary group-hover:text-primary transition-colors duration-200 opacity-60 group-hover:opacity-100" />
              </a>
            </motion.div>
          </div>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="font-body text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto leading-relaxed"
          >
            {content?.tagline}
            <br />
            {content?.subtitle}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-8 pb-24"
          >
            <Button
              variant="default"
              size="lg"
              onClick={scrollToEngagement}
              iconName="UserPlus"
              iconPosition="left"
              className="w-full sm:w-auto hero-cta-button"
            >
              {content?.ctaIstanbul}
            </Button>

            <Button
              variant="outline"
              size="lg"
              onClick={scrollToStates}
              iconName="MapPin"
              iconPosition="left"
              className="w-full sm:w-auto bg-white/10 text-white border-white/20 hover:bg-white/20 hover:text-white"
            >
              {content?.ctaOtherState}
            </Button>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10"
        >
          <motion.button
            onClick={scrollToWhySection}
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="flex flex-col items-center space-y-2 text-white/80 hover:text-white memorial-transition"
          >
            <Icon name="ChevronDown" size={24} />
            <span className="font-body text-sm">{content?.scrollText}</span>
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;